// src/components/FlowTable.js
import React, { useMemo, useState } from 'react';
import Dropdown from './Dropdown';
import ContextMenu from './ContextMenu';
import { jsonToCsv, downloadFile, DIM_KEY_MAP } from '../utils';

// Update the available dimension fields for the dropdown
const FLOW_DIM_FIELDS = ['Mine', 'Intermediary', 'Stage', 'Mineral', 'Year', 'Country'];
const FLOW_METRIC_FIELDS = ['value', 'valueUsd', 'weightTonnes'];

function summarizeFlows(flows, dim, metric) {
  const map = new Map();
  const dataKey = DIM_KEY_MAP[dim] || dim;

  for (const f of flows) {
    let key;
    // Special handling for the derived 'Stage' field
    if (dataKey === 'stage') {
        key = `${f.sourceStage} → ${f.targetStage}`;
    } else if (dataKey === 'country') {
      const keys = [f.sourceCountry, f.targetCountry].filter(c => c && c !== 'Unknown');
      for (const country of [...new Set(keys)]) {
          const v = Number(f[metric]);
          if (!Number.isFinite(v)) continue;
          const cur = map.get(country) || { sum: 0, count: 0 };
          cur.sum += v;
          cur.count += 1;
          map.set(country, cur);
      }
      continue;
    } else {
        key = f[dataKey] ?? '—';
    }

    const v = Number(f[metric]);
    if (!Number.isFinite(v)) continue;

    const cur = map.get(key) || { sum: 0, count: 0 };
    cur.sum += v;
    cur.count += 1;
    map.set(key, cur);
  }
  const out = Array.from(map.entries()).map(([k, {sum, count}]) => ({
    key: k,
    value: sum / Math.max(count, 1) // Using average, but could be sum if needed
  }));
  out.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  return out;
}

export default function FlowTable({ flows, defaultDim = 'Mine', defaultMetric = 'value', selection, onSelectionChange }) {
  const [dim, setDim] = useState(defaultDim);
  const [metric, setMetric] = useState(defaultMetric);
  const data = useMemo(() => summarizeFlows(flows, dim, metric), [flows, dim, metric]);
  const [contextMenu, setContextMenu] = useState(null);

  const handleRowClick = (value) => {
    if (!onSelectionChange || value === '—') return;
    if (selection && selection.dim === dim && selection.value === value) {
      onSelectionChange({ dim: null, value: null });
    } else {
      onSelectionChange({ dim, value });
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (!data?.length) return;

    const downloadCSV = () => {
      const csv = jsonToCsv(data);
      downloadFile(csv, 'flow-table-data.csv', 'text/csv;charset=utf-8;');
    };

    const copyCSV = () => {
      const csv = jsonToCsv(data);
      navigator.clipboard.writeText(csv).catch(err => console.error('Failed to copy CSV: ', err));
    };

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      options: [
        { label: 'Download data as CSV', action: downloadCSV },
        { label: 'Copy data to clipboard', action: copyCSV },
      ]
    });
  };

  return (
    <div className="panel table" onContextMenu={handleContextMenu}>
      <div className="panel-header">
        <div className="panel-title">
          <h3>Flow Breakdown</h3>
          <img src="/assets/rovjok_logo.png" alt="Rovjok Logo" className="header-logo" />
        </div>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>
                <Dropdown value={dim} onChange={setDim} options={FLOW_DIM_FIELDS} />
              </th>
              <th>
                <Dropdown value={metric} onChange={setMetric} options={FLOW_METRIC_FIELDS} />
              </th>
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 250).map((r, i) => {
              const isSelected = selection && selection.dim === dim && selection.value === r.key;
              const isFiltered = selection && selection.dim;

              return (
                <tr 
                  key={r.key + i}
                  onClick={() => handleRowClick(r.key)}
                  className={`${(isFiltered && !isSelected) ? 'muted' : ''} ${isSelected ? 'selected' : ''}`}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{r.key}</td>
                  <td>
                    {Number.isFinite(r.value) 
                      ? r.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                      : '—'
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {contextMenu && <ContextMenu {...contextMenu} onRequestClose={() => setContextMenu(null)} />}
    </div>
  );
}